/**
 * API client — direct calls to the gateway (single origin), bearer injected
 * from the OIDC session. Replaces every Next.js BFF route.
 *
 * On 401: one silent-renew attempt, then retry once; a second 401 surfaces.
 */

import { getConfig } from '@/config';
import { getAccessToken, getUserManager } from '@/auth/oidc';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
  }
}

/**
 * User-facing French message for a caught error. Network failures (fetch
 * rejects with `TypeError: Failed to fetch` when the server is down / a
 * 5xx preflight has no CORS headers / DNS/offline) are reported as
 * status 0 so callers can render a clean "service unavailable" string
 * instead of the raw browser "Failed to fetch".
 */
export function isNetworkError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

/**
 * True for an HTTP 429 (the gateway's per-user rate limit — 120 req/min on QA).
 * Callers that degrade 404/501/503 to an empty state must NOT treat 429 the same
 * way: a rate limit means "retry shortly", not "no data" — swallowing it renders
 * a misleading empty screen (this is exactly how a 429 once surfaced as
 * "Impossible de charger le catalogue de personas").
 */
export function isRateLimited(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429;
}

const NETWORK_DOWN_MESSAGE =
  'Service temporairement indisponible. Vérifiez votre connexion et réessayez.';

const RATE_LIMITED_MESSAGE =
  'Trop de requêtes — limite temporairement atteinte. Patientez un instant puis réessayez.';

async function doFetch(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const { apiBaseUrl } = getConfig();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  try {
    return await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
  } catch {
    // fetch() only rejects on a network-level failure (server unreachable,
    // blocked preflight, offline). Normalise it to a status-0 ApiError with a
    // localized message so no component ever surfaces the raw "Failed to fetch".
    throw new ApiError(0, null, NETWORK_DOWN_MESSAGE);
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let token = await getAccessToken();
  let response = await doFetch(path, init, token);

  if (response.status === 401 && token) {
    try {
      const user = await getUserManager().signinSilent();
      token = user?.access_token ?? null;
      if (token) {
        response = await doFetch(path, init, token);
      }
    } catch {
      // silent renew failed — the 401 below surfaces and the guard re-logs-in
    }
  }

  if (!response.ok) {
    // Read the body ONCE as text, then try JSON — response.json() consumes the
    // stream even on parse failure, which would make a text() fallback throw.
    let body: unknown = null;
    const raw = await response.text().catch(() => '');
    try {
      body = raw ? JSON.parse(raw) : null;
    } catch {
      body = raw || null;
    }
    if (response.status === 429) {
      // Give a rate limit a localized, actionable message (honouring Retry-After
      // when the limiter sends it) instead of the opaque "API error 429".
      const retryAfter = response.headers.get('Retry-After');
      const suffix = retryAfter ? ` (réessayez dans ${retryAfter}s)` : '';
      throw new ApiError(429, body, RATE_LIMITED_MESSAGE + suffix);
    }
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  // `init` lets a caller opt into request options such as `keepalive` (so a POST
  // fired optimistically completes even if the page navigates/reloads right
  // after). method + body always win over anything spread from init.
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  // `init` lets a caller opt into request options such as `keepalive` (so an
  // optimistic PATCH completes even if the page navigates/reloads right after).
  // method + body always win over anything spread from init.
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
